/**
 *
 * The shadowDom / Intersection Observer version of Paul's concept:
 * https://github.com/paulirish/lite-youtube-embed
 *
 * A lightweight YouTube / Vimeo embed. Still should feel the same to the user, just
 * MUCH faster to initialize and paint.
 *
 * Thx to these as the inspiration
 *   https://storage.googleapis.com/amp-vs-non-amp/youtube-lazy.html
 *   https://autoplay-youtube-player.glitch.me/
 *   https://github.com/justinribeiro/lite-youtube/
 *   https://github.com/slightlyoff/lite-vimeo/
 * 
 * 
 */

import EventHandler from '../../utils/dom/event-handler';
import SelectorEngine from '../../utils/dom/selector-engine';
import dataLayer from '../../utils/data-layer';
import Manipulator from '../../utils/dom/manipulator';
import { VIMEO_ACCESS_TOKEN } from '../../utils/constants';
import Request from '../../utils/request';

export class LiteVideoEmbed extends HTMLElement {
	constructor() {
		super();
		this.iframeLoaded = false;
		this.thrdAccept;
		this.maxWatchedTime = 0;
	}

	static get observedAttributes() {
		return ['videotype', 'videoid'];
	}

	connectedCallback() {
		this.setupDom();
		this.initEvents();
		this.cookieHandler();
		this.addEventListener('pointerover', LiteVideoEmbed.warmConnections, {
			once: true
		});
		this.addEventListener('click', () => this.addIframe());
		
		// If videoid is already set when element connects, initialize the component
		// This ensures setupComponent() is called after shadow DOM is ready
		if (this.getAttribute('videoid') && this.shadowRoot && this.domRefImg) {
			this.setupComponent();
		}	
	}
	get mandatoryWatch() {
		return this.hasAttribute('mandatory-watch');
	}
	get fallbackThumbnail() {
		return this.getAttribute('fallback-thumbnail') || '';
	}

	set fallbackThumbnail(thumbnail) {
		this.setAttribute('fallback-thumbnail', thumbnail);
	}

	get videoType() {
		return encodeURIComponent(this.getAttribute('videotype') || '');
	}

	set videoType(type) {
		this.setAttribute('videotype', type);
	}

	get videoId() {
		return encodeURIComponent(this.getAttribute('videoid') || '');
	}

	set videoId(id) {
		this.setAttribute('videoid', id);
	}

	get videoTitle() {
		return this.getAttribute('videotitle') || 'Video';
	}

	set videoTitle(title) {
		this.setAttribute('videotitle', title);
	}

	get videoPlay() {
		return this.getAttribute('videoplay') || 'Play';
	}

	set videoPlay(name) {
		this.setAttribute('videoplay', name);
	}

	get videoStartAt() {
		return Number(this.getAttribute('videoStartAt') || '0');
	}

	set videoStartAt(time) {
		this.setAttribute('videoStartAt', String(time));
	}

	get autoLoad() {
		return this.hasAttribute('autoload');
	}

	set autoLoad(value) {
		if (value) {
			this.setAttribute('autoload', '');
		} else {
			this.removeAttribute('autoload');
		}
	}

	get autoPlay() {
		return this.hasAttribute('autoplay');
	}

	set autoPlay(value) {
		if (value) {
			this.setAttribute('autoplay', 'autoplay');
		} else {
			this.removeAttribute('autoplay');
		}
	}

	get params() {
		return `start=${this.videoStartAt}&${this.getAttribute('params')}`;
	}

	/**
	 * Define our shadowDOM for the component
	 */
	setupDom() {
		const shadowDom = this.attachShadow({ mode: 'open' });
		shadowDom.innerHTML = `
		<style>
			:host {
				contain: content;
				display: block;
				position: relative;
				width: 100%;
				padding-bottom: calc(100% / (16 / 9));
			}
			:host-context(.swiper-slide:not(.swiper-slide-active)) iframe,
			:host-context(.swiper-slide:not(.swiper-slide-active)) .lty-playbtn {
				display: block;
			}
			#frame, #fallbackPlaceholder, iframe {
				position: absolute;
				width: 100%;
				height: 100%;
			}
			#frame {
				cursor: pointer;
			}
			#fallbackPlaceholder {
				object-fit: cover;
			}
			#frame::before {
				content: '';
				display: block;
				position: absolute;
				top: 0;
				background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAADGCAYAAAAT+OqFAAAAdklEQVQoz42QQQ7AIAgEF/T/D+kbq/RWAlnQyyazA4aoAB4FsBSA/bFjuF1EOL7VbrIrBuusmrt4ZZORfb6ehbWdnRHEIiITaEUKa5EJqUakRSaEYBJSCY2dEstQY7AuxahwXFrvZmWl2rh4JZ07z9dLtesfNj5q0FU3A5ObbwAAAABJRU5ErkJggg==);
				background-position: top;
				background-repeat: repeat-x;
				height: 60px;
				padding-bottom: 50px;
				width: 100%;
				transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
				z-index: 1;
			}
			/* play button */
			.lty-playbtn {
				/*width: 70px;
				height: 46px;*/
				/*background-color: #212121;*/
				background-color: transparent;
				z-index: 1;
				opacity: 0.8;
				/*border-radius: 14%; *//* TODO: Consider replacing this with YT's actual svg. Eh. */
				transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
				border: 0;
				font-size: 4rem;
				color: #16316F; /* lta-dark-blue */
				cursor: pointer;
				display: flex;
				align-items: center;
				justify-content: center;
			}

			@media (max-width: 767.98px) {
				.lty-playbtn {
					font-size: 3.5rem;
				}
			}

			#frame:hover .lty-playbtn {
				/*background-color: #f00;*/
				opacity: 1;
			}
			/* play button triangle */
			/*.lty-playbtn:before {
				content: '';
				border-style: solid;
				border-width: 11px 0 11px 19px;
				border-color: transparent transparent transparent #fff;
			}*/
			.lty-playbtn,
			.lty-playbtn:before {
				position: absolute;
				top: 50%;
				left: 50%;
				transform: translate3d(-50%, -50%, 0);
			}

			@media (max-width: 767.98px) {
				.lty-playbtn {
					font-size: 3.5rem;
				}
			}

			/* Post-click styles */
			.lyt-activated {
				cursor: unset;
			}
			#frame.lyt-activated::before,
			.lyt-activated .lty-playbtn {
				display: none !important;
			}
		</style>
		<div id="frame">
		  <picture>
			<source id="webpPlaceholder" type="image/webp">
			<source id="jpegPlaceholder" type="image/jpeg">
			<img id="fallbackPlaceholder" referrerpolicy="origin">
		  </picture>
		  <button class="lty-playbtn">
		  	<svg class="fe-icon" width="1em" height="1em" fill="currentColor">
			  <use xlink:href="/static/images/icons/lta-icons.svg#arrow-right-circle-play"></use>
			</svg>
		  </button>
		</div>
	  `;
		this.domRefFrame = this.shadowRoot.querySelector('#frame');
		this.domRefImg = {
			fallback: this.shadowRoot.querySelector('#fallbackPlaceholder'),
			webp: this.shadowRoot.querySelector('#webpPlaceholder'),
			jpeg: this.shadowRoot.querySelector('#jpegPlaceholder')
		};
		this.domRefPlayButton = this.shadowRoot.querySelector('.lty-playbtn');
		this.videoWrapper = this.shadowRoot.host.closest('figure');
		this.isPrivate = Manipulator.getDataAttribute(this.videoWrapper, 'is-private');
		this.privateAuthKey = Manipulator.getDataAttribute(this.videoWrapper, 'private-auth-key');
	}

	initEvents() {
		this.addEventListener('lta-litevideo-event', (event) => {
			const { command } = event.detail;

			this.onVideoCommand(command);
		});

		EventHandler.on(this.domRefPlayButton, 'click', (event) => {
			const liteVideoElement = event.target.getRootNode().host.closest('figure');
			const videoNameAttr = Manipulator.getDataAttribute(liteVideoElement, 'video-name');
			const videoDescAttr = Manipulator.getDataAttribute(liteVideoElement, 'video-description');
			const videoUrlAttr = Manipulator.getDataAttribute(liteVideoElement, 'video-url');
			const videoDateAttr = Manipulator.getDataAttribute(liteVideoElement, 'video-date');
			const videoEmbedAttr = {
				'videoName': videoNameAttr,
				'videoDescription': videoDescAttr,
				'videoURL': videoUrlAttr,
				'VideoDate': videoDateAttr
			};
			dataLayer.push({ 'event': 'videoView', ...videoEmbedAttr });
		})
	}

	/**
	 * Parse our attributes and fire up some placeholders
	 */
	setupComponent() {
		// Ensure shadow DOM is ready before trying to access DOM references
		if (!this.shadowRoot || !this.domRefImg || !this.domRefImg.fallback || !this.domRefPlayButton) {
			// Shadow DOM not ready yet, skip initialization
			// It will be called again from connectedCallback when ready
			return;
		}
		
		this.initImagePlaceholder();
		this.domRefPlayButton.setAttribute('aria-label', `${this.videoPlay}: ${this.videoTitle}`);
		this.setAttribute('title', `${this.videoPlay}: ${this.videoTitle}`);
		if (this.autoLoad) {
			this.initIntersectionObserver();
		}
	}

	/**
	 * Lifecycle method that we use to listen for attribute changes to period
	 *
	 * @param {*} name
	 * @param {*} oldVal
	 * @param {*} newVal
	 */
	attributeChangedCallback(name, oldVal, newVal) {
		switch (name) {
			case 'videoid': {
				if (oldVal !== newVal) {
					this.setupComponent();
					// if we have a previous iframe, remove it and the activated class
					if (this.domRefFrame.classList.contains('lyt-activated')) {
						this.domRefFrame.classList.remove('lyt-activated');
						this.classList.remove('lyt-activated');
						this.shadowRoot.querySelector('iframe').remove();
						this.iframeLoaded = false;
					}
				}
				break;
			}
			// eslint-disable-next-line no-fallthrough
			default:
				break;
		}
	}

	onVideoCommand(command) {
		const iframe = this.shadowRoot.querySelector('iframe');

		if (iframe) {
			switch (command) {
				case 'pause':
					if (this.videoType === 'youtube') {
						iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
					} else if (this.videoType === 'vimeo') {
						iframe.contentWindow.postMessage('{"method":"pause","value":"true"}', '*');
					}
					break;
				case 'play':
					if (this.videoType === 'youtube') {
						iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
					} else if (this.videoType === 'vimeo') {
						iframe.contentWindow.postMessage('{"method":"play"}', '*');
					}
					break;
				default:
					break;
			}
		}
	}

	cookieHandler() {
		import('js-cookie').then(({ default: Cookies }) => {
			this.thrdAccept = Cookies.get('cookieprefs') ? JSON.parse(Cookies.get('cookieprefs'))['thrd'] : false

			return 1;
		}).catch((error) => {
			console.log(error);
		});
	}

	loadYouTubeApi() {
		return new Promise((resolve) => {
			if (window.YT && window.YT.Player) {
				resolve();
			}
			const script = document.createElement('script');
			script.src = 'https://www.youtube.com/iframe_api';
			document.body.appendChild(script);
			window.onYouTubeIframeAPIReady = () => {
				resolve();
			};
		});
	}

	async initializeYouTubeTracking() {
		await this.loadYouTubeApi();
		const iframe = this.shadowRoot.querySelector('iframe');
		if (!iframe) {
			return;
		}

		this.player = new window.YT.Player(iframe, {
			events: {
				onReady: () => {
					this.progressInterval = setInterval(() => {
					const currentTime = this.player.getCurrentTime();

					if (currentTime > this.maxWatchedTime + 2) {
						this.player.seekTo(this.maxWatchedTime, true);
						return;
					}

					if (currentTime > this.maxWatchedTime) {
						this.maxWatchedTime = currentTime;
					}
				}, 500);
				},
				onStateChange: this.onYouTubeStateChange.bind(this)
			}
		});
	}

	disconnectedCallback() {
		if (this.progressInterval) {
			clearInterval(this.progressInterval);
		}
	}

	startTracking() {
		if (this.progressInterval) {
			return;
		}

		this.progressInterval = setInterval(() => {
			const currentTime = this.player.getCurrentTime();

			if (currentTime > this.maxWatchedTime + 2) {
				this.player.seekTo(this.maxWatchedTime, true);
				return;
			}

			if (currentTime > this.maxWatchedTime) {
				this.maxWatchedTime = currentTime;
			}
		}, 500);
	}

	stopTracking() {
		if (this.progressInterval) {
			clearInterval(this.progressInterval);
			this.progressInterval = null;
		}
	}

	onYouTubeStateChange(event) {
		switch (event.data) {
			case window.YT.PlayerState.PLAYING:
				this.startTracking();
				break;
			case window.YT.PlayerState.PAUSED:
				this.stopTracking();
				break;
			case window.YT.PlayerState.ENDED:
				this.stopTracking();
				this.onMandatoryVideoCompleted();
				break;
			default:
				break;
		}
	}

	onMandatoryVideoCompleted() {
		if (this.progressInterval) {
			clearInterval(this.progressInterval);
			this.progressInterval = null;
		}

		this.dispatchEvent(
			new CustomEvent('mandatory-video-completed', {
				bubbles: true,
				composed: true,
				detail: {
					videoId: this.videoId,
				}
			})
		);
	}
	/**
	 * Inject the iframe into the component body
	 */
	addIframe() {
		// Addition for dealing with the case where the third party cookies are not accepted

		if (!this.iframeLoaded) {
			let iframeHTML;

			if (this.videoType === 'youtube') {
				iframeHTML = `
<iframe frameborder="0"
  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen
  src="https://www.youtube` + (this.thrdAccept ? `` : `-nocookie`) + `.com/embed/${this.videoId}?autoplay=1&enablejsapi=1&origin=${window.location.origin}&${this.params}"></iframe>`;

			} else if (this.videoType === 'vimeo') {
				const apValue = ((this.autoLoad && this.autoPlay) || (!this.autoLoad)) ? 'autoplay=1' : '';
				const srcUrl = new URL(
					`/video/${this.videoId}?${apValue}&#t=${this.videoStartAt}&api=1`,
					'https://player.vimeo.com/'
				);

				// TODO: construct src value w/ URL constructor
				iframeHTML = `
<iframe frameborder="0"
  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen ` + (this.thrdAccept ? `` : `dnt="true"`) + `
  src="${srcUrl}"></iframe>`;
			}

			this.domRefFrame.insertAdjacentHTML('beforeend', iframeHTML);
			const iframe = this.shadowRoot.querySelector('iframe');
			this.domRefFrame.classList.add('lyt-activated');
			this.classList.add('lyt-activated');
			this.iframeLoaded = true;
			if (this.videoType === 'youtube' && this.mandatoryWatch) {
				this.initializeYouTubeTracking();
			}
		}
	}

	/**
	 * Setup the placeholder image for the component
	 */
	async initImagePlaceholder() {

		// we don't know which image type to preload, so warm the connection
		let posterUrlWebp;
		let posterUrlJpeg;

		if (this.videoType === 'youtube') {
			LiteVideoEmbed.addPrefetch('preconnect', 'https://i.ytimg.com/');
			posterUrlWebp = `https://i.ytimg.com/vi_webp/${this.videoId}/maxresdefault.webp`; // hqdefault
			posterUrlJpeg = `https://i.ytimg.com/vi/${this.videoId}/maxresdefault.jpg`;
		} else if (this.videoType === 'vimeo') {
			LiteVideoEmbed.addPrefetch('preconnect', 'https://i.vimeocdn.com/');
			const myHeaders = new Headers();
			if (this.isPrivate) {
				myHeaders.append('Authorization', `Bearer ${this.privateAuthKey || VIMEO_ACCESS_TOKEN}`);
			}
			const requestOptions = {
				method: 'GET',
				headers: myHeaders
			};

			await fetch(`https://api.vimeo.com/videos/${this.videoId}/pictures`, requestOptions)
				.then((response) => response.json())
				// eslint-disable-next-line promise/always-return
				.then((result) => {
					// eslint-disable-next-line promise/always-return
					if (result?.data?.length > 0) {
						const tnLarge = result?.data[0]?.sizes[0]?.link;
						const imgId = tnLarge.slice(tnLarge.lastIndexOf('/') + 1).split('_')[0];
						posterUrlWebp = `https://i.vimeocdn.com/video/${imgId}.webp?mw=1100&mh=619&q=70`;
						posterUrlJpeg = `https://i.vimeocdn.com/video/${imgId}.jpg?mw=1100&mh=619&q=70`;
					}

				})
				.catch((error) => console.error(error));
		}

		if (!this.fallbackThumbnail) {
			this.domRefImg.webp.srcset = posterUrlWebp;
			this.domRefImg.jpeg.srcset = posterUrlJpeg;
			this.domRefImg.fallback.src = posterUrlJpeg;
		} else {
			this.domRefImg.fallback.src = this.fallbackThumbnail;
		}
		this.domRefImg.fallback.setAttribute('aria-label', `${this.videoPlay}: ${this.videoTitle}`);
		this.domRefImg.fallback.setAttribute('alt', `${this.videoPlay}: ${this.videoTitle}`);
	}

	/**
	 * Setup the Intersection Observer to load the iframe when scrolled into view
	 */
	initIntersectionObserver() {
		if ('IntersectionObserver' in window && 'IntersectionObserverEntry' in window) {
			const options = {
				root: null,
				rootMargin: '0px',
				threshold: 0
			};
			const observer = new IntersectionObserver((entries, observer) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting && !this.iframeLoaded) {
						LiteVideoEmbed.warmConnections();
						this.addIframe();
						observer.unobserve(this);
					}
				});
			}, options);
			observer.observe(this);
		}
	}

	/**
	 * Add a <link rel={preload | preconnect} ...> to the head
	 * @param {*} kind
	 * @param {*} url
	 * @param {*} as
	 */
	static addPrefetch(kind, url, as) {
		const linkElem = document.createElement('link');
		linkElem.rel = kind;
		linkElem.href = url;
		if (as) {
			linkElem.as = as;
		}
		linkElem.crossOrigin = 'true';
		document.head.append(linkElem);
	}

	/**
	 * Begin preconnecting to warm up the iframe load Since the embed's netwok
	 * requests load within its iframe, preload/prefetch'ing them outside the
	 * iframe will only cause double-downloads. So, the best we can do is warm up
	 * a few connections to origins that are in the critical path.
	 *
	 * Maybe `<link rel=preload as=document>` would work, but it's unsupported:
	 * http://crbug.com/593267 But TBH, I don't think it'll happen soon with Site
	 * Isolation and split caches adding serious complexity.
	 */
	static warmConnections() {
		if (LiteVideoEmbed.preconnected) return;

		if (this.videoType === 'youtube') {
			// Host that YT uses to serve JS needed by player, per amp-youtube
			LiteVideoEmbed.addPrefetch('preconnect', 'https://s.ytimg.com');

			// The iframe document and most of its subresources come right off
			// youtube.com
			LiteVideoEmbed.addPrefetch('preconnect', 'https://www.youtube.com');

			// The botguard script is fetched off from google.com
			LiteVideoEmbed.addPrefetch('preconnect', 'https://www.google.com');

			// TODO: Not certain if these ad related domains are in the critical path.
			// Could verify with domain-specific throttling.
			LiteVideoEmbed.addPrefetch('preconnect', 'https://googleads.g.doubleclick.net');
			LiteVideoEmbed.addPrefetch('preconnect', 'https://static.doubleclick.net');
		} else if (this.videoType === 'vimeo') {
			// Host that Vimeo uses to serve JS needed by player
			LiteVideoEmbed.addPrefetch('preconnect', 'https://f.vimeocdn.com');
			// The iframe document comes from player.vimeo.com
			LiteVideoEmbed.addPrefetch('preconnect', 'https://player.vimeo.com');
			// Image for placeholder comes from i.vimeocdn.com
			LiteVideoEmbed.addPrefetch('preconnect', 'https://i.vimeocdn.com');
		}

		LiteVideoEmbed.preconnected = true;
	}
}

LiteVideoEmbed.preconnected = false;

// Register custom element
customElements.define('lite-video', LiteVideoEmbed);
